import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// internal import
import apiLink from "../../../api/apiLink";
import Loader from "../../../components/common/Loader";
import { smsCount } from "../../../components/common/UtilityMethods";
import { smsSettingUpdateIsp } from "../../../features/authSlice";
import useISPowner from "../../../hooks/useISPOwner";
import AudioUploadPlayer from "./AudioUploadPlayer";
import { audioFileUpload } from "../../../features/apiCallAdmin";

const MAX_SMS_LENGTH = 334;

const DAY_OPTIONS = [
  { n: 1, id: "OneDay", label: "billDueOneDay", tpl: "onedayTemplate" },
  { n: 2, id: "TwoDay", label: "billDueTwoDay", tpl: "twodaysTemplate" },
  { n: 3, id: "ThreeDay", label: "billDueThreeDay", tpl: "threedaysTemplate" },
  { n: 4, id: "FourDay", label: "billDueFourDay", tpl: "fourdaysTemplate" },
  { n: 5, id: "FiveDay", label: "billDueFiveDay", tpl: "fivedaysTemplate" },
  { n: 6, id: "SixDay", label: "billDueSixDay", tpl: "sixdaysTemplate" },
  { n: 7, id: "SevenDay", label: "billDueSevenDay", tpl: "sevendaysTemplate" },
];

const BASE_TEMPLATE_ITEMS = [
  { id: "USERNAME", value: "USER: USERNAME" },
  { id: "CUSTOMER_ID", value: "ID: CUSTOMER_ID" },
  { id: "CUSTOMER_NAME", value: "NAME: CUSTOMER_NAME" },
  { id: "AMOUNT", value: "BILL: AMOUNT" },
  { id: "BILL_DATE", value: "LAST DATE: BILL_DATE" },
];

const LEGACY_PLACEHOLDERS = [
  "ইউজারনেমঃ USERNAME",
  "ইউজার আইডিঃ USERID",
  "গ্রাহকঃ NAME",
  "বিলঃ AMOUNT",
  "তারিখঃ DATE",
];

const dayKey = (n) => `alert${n}`;
const dayNumber = (key) => Number(String(key).replace("alert", ""));

const AlertSmsTemplate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = useRef();

  //---> @Get user & current user data form useISPowner hooks
  const { ispOwnerData, ispOwnerId, bpSettings, settings } = useISPowner();

  const [loading, setLoading] = useState(false);

  const [messageType, setMessageType] = useState("");
  const [sendingType, setSendingType] = useState("");
  const [billConfirmation, setBillConfirmation] = useState("");
  const [days, setDays] = useState([]);
  const [alertNum, setAlertNum] = useState("");

  // text template
  const [fontValue, setFontValue] = useState("");
  const [bottomText, setBottomText] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);

  const [audioByDay, setAudioByDay] = useState({});

  const currentFile = audioByDay[alertNum];
  const savedAudio = settings?.sms?.voiceTemplate?.[alertNum];

  const messageTypes = useMemo(
    () => [
      { label: t("text"), value: "text" },
      ...(bpSettings?.voiceMessage
        ? [{ label: t("voice"), value: "voice" }]
        : []),
    ],
    [t],
  );

  const sendingMessageTypes = useMemo(
    () => [
      { label: t("nonMasking"), value: "nonMasking" },
      { label: t("masking"), value: "masking" },
      { label: t("fixedNumber"), value: "fixedNumber" },
      ...(bpSettings?.hasSmsGateway
        ? [{ label: t("personalGateway"), value: "personalGateway" }]
        : []),
    ],
    [t, bpSettings?.hasSmsGateway],
  );

  const customerPaymentLink = `Payment Link: https://app.netfeebd.com/isp/${ispOwnerData?.netFeeId}`;
  const templateItems = useMemo(
    () => [
      ...BASE_TEMPLATE_ITEMS,
      ...(ispOwnerData?.bpSettings?.hasPG
        ? [
            {
              id: "PAYMENT_LINK",
              value: customerPaymentLink,
              label: "PAYMENT_LINK",
            },
          ]
        : []),
    ],
    [ispOwnerData?.bpSettings?.hasPG, customerPaymentLink],
  );

  const upperText = useMemo(
    () => selectedItems.map((item) => `\n${item}`).join(""),
    [selectedItems],
  );

  const fullText = fontValue + upperText + bottomText;
  const bottomMaxLength = Math.max(
    0,
    MAX_SMS_LENGTH + 1 - fontValue.length - upperText.length,
  );

  const resetTemplateState = useCallback(() => {
    setAlertNum("");
    setSelectedItems([]);
    setFontValue("");
    setBottomText("");
  }, []);

  const getSavedDays = useCallback(
    (type) =>
      (type === "voice"
        ? settings?.sms?.voiceAlertDays
        : settings?.sms?.alertDays) || [],
    [settings],
  );

  useEffect(() => {
    const sms = settings?.sms;
    setMessageType(sms?.alertMessageType || "");
    setSendingType(sms?.alertSendBy || "");
    setBillConfirmation(sms?.alert ? "on" : "off");
    setDays(getSavedDays(sms?.alertMessageType));
  }, [settings, getSavedDays]);

  useEffect(() => {
    if (alertNum && !days.includes(dayNumber(alertNum))) {
      resetTemplateState();
    }
  }, [days, alertNum, resetTemplateState]);

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    setDays(getSavedDays(type));
    resetTemplateState();
  };

  const daySettingHandler = (n) => {
    setDays((prev) =>
      prev.includes(n) ? prev.filter((d) => d !== n) : [...prev, n],
    );
  };

  const itemSettingHandler = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item));
      return;
    }

    const nextLength =
      (fontValue + "\n" + upperText + "\n" + bottomText).length + item.length;

    if (nextLength > MAX_SMS_LENGTH) {
      toast.error(t("exceedSMSLimit"));
      return;
    }
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleAudioChange = (file) => {
    if (!alertNum) {
      toast.error(t("selectTemplate"));
      return;
    }
    setAudioByDay((prev) => {
      const next = { ...prev };
      if (file) next[alertNum] = file;
      else delete next[alertNum];
      return next;
    });
  };

  const dayTempletHandler = (key) => {
    if (messageType === "voice") {
      setAlertNum(key);
      return;
    }

    if (!key) {
      resetTemplateState();
      return;
    }

    const raw = settings?.sms?.template?.[key] || "";

    let cleaned = raw;
    LEGACY_PLACEHOLDERS.forEach((p) => {
      cleaned = cleaned.replace(p, "");
    });
    cleaned = cleaned.replace("Payment_link", customerPaymentLink);

    const allowedItems = templateItems.map((i) => i.value);
    const items = cleaned
      .split("\n")
      .filter((line) => allowedItems.includes(line));

    let messageBody = cleaned;
    items.forEach((item) => {
      messageBody = messageBody.replace(item, "");
    });

    const lines = messageBody.split("\n");
    const bottom = lines
      .slice(2)
      .filter((line) => line !== "")
      .map((line) => line + "\n")
      .join("");

    setAlertNum(key);
    setSelectedItems(items);
    setFontValue(lines[0] || "");
    setBottomText(bottom);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!messageType) {
      toast.error(t("messageType"));
      return;
    }

    const newFile = messageType === "voice" ? audioByDay[alertNum] : null;

    if (messageType === "voice") {
      if (!alertNum) {
        toast.error(t("selectTemplate"));
        return;
      }
      if (!newFile && !savedAudio) {
        toast.error("Voice file is required.");
        return;
      }
    }

    const data = {
      ...settings?.sms,
      alertMessageType: messageType,
      alertSendBy: sendingType,
      alert:
        billConfirmation === "on"
          ? true
          : billConfirmation === "off"
            ? false
            : null,
      ...(messageType === "voice"
        ? { voiceAlertDays: days }
        : { alertDays: days }),
    };

    if (messageType === "text") {
      const body = upperText + "\n" + bottomText;
      data.template = {
        ...settings?.sms?.template,
        alert: body,
        ...(alertNum ? { [alertNum]: fontValue + body } : {}),
      };
    }

    setLoading(true);
    try {
      if (newFile) {
        const formData = new FormData();
        formData.append("file", newFile);

        const oldPublicId = settings?.sms?.voiceTemplate?.[alertNum]?.publicID;
        if (oldPublicId) {
          formData.append("publicId", oldPublicId);
        }

        const uploaded = await audioFileUpload(setLoading, formData);
        const f = uploaded?.[0];

        if (!f) {
          toast.error("Voice file upload failed.");
          return;
        }
        setLoading(true);

        data.voiceTemplate = {
          ...settings?.sms?.voiceTemplate,
          [alertNum]: {
            originalName: f.originalName,
            path: f.path,
            publicID: f.publicID,
            duration: f.duration,
          },
        };
      }

      const res = await apiLink.patch(
        `/ispOwner/settings/sms/${ispOwnerId}`,
        data,
      );
      dispatch(smsSettingUpdateIsp(res.data));

      if (newFile) {
        setAudioByDay((prev) => {
          const next = { ...prev };
          delete next[alertNum];
          return next;
        });
      }

      toast.success(t("alertSMStemplateSaveAlert"));
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form ref={formRef} onSubmit={handleSubmit} className="settingForm">
        <div className="writeMessageSection">
          <div className="messageStatus d-flex justify-content-between">
            <div className="message-sending-type">
              <h5>{t("messageType")}</h5>
              <div className="smsType">
                {messageTypes.map((type) => (
                  <div className="message_radio" key={type.value}>
                    <input
                      type="radio"
                      id={type.value + "calender"}
                      name="messageType"
                      value={type.value}
                      onChange={(e) => handleMessageTypeChange(e.target.value)}
                      checked={messageType === type.value}
                    />
                    <label htmlFor={type.value + "calender"}>
                      {type.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="sending-status">
              <h4> {t("alertSMStemplate")} </h4>
              <div className="displayGrid1">
                {[
                  { id: "onTemplate", value: "on", label: t("ON") },
                  { id: "offTemplate", value: "off", label: t("OFF") },
                ].map((opt) => (
                  <div className="message_radio" key={opt.id}>
                    <input
                      type="radio"
                      name="billConfirmation"
                      id={opt.id}
                      value={opt.value}
                      checked={billConfirmation === opt.value}
                      onChange={(e) => setBillConfirmation(e.target.value)}
                    />
                    <label htmlFor={opt.id}>{opt.label}</label>
                  </div>
                ))}
              </div>
            </div>

            {messageType === "text" && (
              <div className="message-sending-type">
                <h4>{t("sendingMessageType")}</h4>
                <div className="smsType">
                  {sendingMessageTypes.map((type) => (
                    <div className="message_radio" key={type.value}>
                      <input
                        type="radio"
                        name="sendingType"
                        id={type.value + "calender"}
                        value={type.value}
                        onChange={(e) => setSendingType(e.target.value)}
                        checked={sendingType === type.value}
                      />
                      <label htmlFor={type.value + "calender"}>
                        {type.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="h6 mt-3"> {t("dueToFinishBillCycle")} </p>

            <div className="displayGrid d-flex mb-3">
              {DAY_OPTIONS.map((day) => (
                <div className="day_checkbox" key={day.id}>
                  <input
                    type="checkbox"
                    className="getValueUsingClass"
                    id={day.id}
                    checked={days.includes(day.n)}
                    onChange={() => daySettingHandler(day.n)}
                  />
                  <label htmlFor={day.id}>{t(day.label)}</label>
                </div>
              ))}
            </div>

            <div className="displayGrid">
              <select
                className="form-select mw-100 mt-0"
                value={alertNum}
                onChange={(e) => dayTempletHandler(e.target.value)}
                style={{ backgroundColor: "#dcdcdc" }}
              >
                <option value=""> {t("selectTemplate")} </option>
                {DAY_OPTIONS.filter((day) => days.includes(day.n)).map(
                  (day) => (
                    <option key={day.n} value={dayKey(day.n)}>
                      {t(day.tpl)}
                    </option>
                  ),
                )}
              </select>
            </div>
          </div>

          {messageType === "text" ? (
            <div className="mt-2">
              <input
                value={fontValue}
                onChange={(e) => setFontValue(e.target.value)}
                className="form-control w-25"
                type="text"
                placeholder={t("title")}
                maxLength={40}
              />

              <div className="billconfirm">
                <div className="showthesequence ">
                  <p className="endingText">{fontValue}</p>
                  {selectedItems.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p className="endingtext">{bottomText}</p>
                </div>

                <div className="displayFlexx">
                  {templateItems.map((item) => (
                    <div className="radioselect" key={item.id}>
                      <input
                        id={item.id}
                        type="checkbox"
                        className="getValueUsingClass"
                        value={item.value}
                        checked={selectedItems.includes(item.value)}
                        onChange={(e) => itemSettingHandler(e.target.value)}
                      />
                      <label className="templatelabel" htmlFor={item.id}>
                        {item.label || item.value}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="smsCount">
                <span className="smsLength">
                  {t("letter")}
                  {fullText.length}
                </span>
                <span>SMS: {smsCount(fullText)}</span>
              </div>

              <textarea
                id="messageTextArea"
                rows="6"
                className="form-control mt-4"
                placeholder={t("messageLikhun")}
                value={bottomText}
                maxLength={bottomMaxLength}
                onChange={(e) => setBottomText(e.target.value)}
              ></textarea>
            </div>
          ) : messageType === "voice" ? (
            alertNum ? (
              <AudioUploadPlayer
                key={alertNum}
                file={currentFile}
                savedAudio={savedAudio}
                onFileChange={handleAudioChange}
              />
            ) : (
              <p className="text-muted text-center mt-4">
                {t("selectTemplate")}
              </p>
            )
          ) : null}

          <button
            type="submit"
            className="btn btn-success mt-4"
            disabled={loading}
          >
            {loading ? <Loader /> : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AlertSmsTemplate;
